// Sidebar.tsx
import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  IconHelp,
  IconLogout,
  IconLoader2,
  IconChevronUp,
  IconChevronDown,
  IconLayoutSidebarLeftExpand,
  IconLayoutSidebarLeftCollapse,
} from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import CustomImage from '@/components/CustomImage';
import { sidebarMenu } from '@/constants/menuData';
import { EUserRole } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import useWindowSize from '@/hooks/useWindowSize';
import { logoutApi } from '@/service/authService';
import { getParentChildren } from '@/service/parentSettingsServices';
import { useParentStore } from '@/store/useParentStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import useUser from '@/store/useUser';
import { IParentChild } from '@/types/parent';
import { generateOrGetUserId } from '@/utils';
const JUSTIFY_CENTER = 'justify-center';
const TEXT_WHITE = 'text-white';
const CURSOR_NOT_ALLOWED = 'cursor-not-allowed!';

type TMenuId = number | string | null;

interface IMenuItem {
  id: number | string;
  name: string;
  icon?: React.ElementType;
  link?: string;
  disabled?: boolean;
  hidden?: boolean;
  onlyForAdmin?: boolean;
  onlyForNonAdmin?: boolean;
  children?: IMenuItem[];
  allowedRoles?: EUserRole[];
  roleLinks?: Partial<Record<EUserRole, string>>;
}

interface IChildOption {
  id: string;
  name: string;
  age: number;
  profile_img?: string;
}

interface IParentChildSwitcherProps {
  collapsed: boolean;
  children: IChildOption[];
  selectedChild: IChildOption;
  selectedChildId: string;
  isChildSwitcherOpen: boolean;
  childSwitcherRef: React.RefObject<HTMLDivElement | null>;
  formatText: (id: string) => string;
  onToggle: () => void;
  onSelectChild: (childId: string) => void;
}

const ParentChildSwitcher = ({
  collapsed,
  children,
  selectedChild,
  selectedChildId,
  isChildSwitcherOpen,
  childSwitcherRef,
  formatText,
  onToggle,
  onSelectChild,
}: IParentChildSwitcherProps) => {
  return (
    <div
      className={clsx('mb-3 relative', collapsed ? 'w-15' : 'w-full')}
      ref={childSwitcherRef}
    >
      <button
        type="button"
        className={clsx(
          'w-full rounded-lg border border-neutral-light-200 bg-white cursor-pointer',
          collapsed
            ? 'relative flex items-center justify-center p-1.5'
            : 'flex items-center gap-2 p-2'
        )}
        onClick={onToggle}
        data-testid="child-switcher"
      >
        {selectedChild.profile_img ? (
          <img
            src={selectedChild.profile_img}
            alt={selectedChild.name}
            className={clsx(
              'rounded-md border border-neutral-light-100 object-cover',
              collapsed ? 'h-9 w-9' : 'h-10 w-10'
            )}
          />
        ) : (
          <div
            className={clsx(
              'rounded-md border border-neutral-light-100 bg-neutral-light-50 flex items-center justify-center text-xs font-semibold text-neutral-primary',
              collapsed ? 'h-9 w-9' : 'h-10 w-10'
            )}
          >
            {selectedChild.name
              .split(' ')
              .slice(0, 2)
              .map((word) => word.charAt(0))
              .join('')
              .toUpperCase()}
          </div>
        )}

        {!collapsed && (
          <div className="flex-1 text-start">
            <p className="text-sm font-semibold text-neutral-primary">
              {selectedChild.name}
            </p>
            <p className="text-xs text-neutral-secondary">
              {selectedChild.age} {formatText('sidebar.years')}
            </p>
          </div>
        )}

        {!collapsed && (
          <span>
            {isChildSwitcherOpen ? (
              <IconChevronUp size={16} className="text-neutral-secondary" />
            ) : (
              <IconChevronDown size={16} className="text-neutral-secondary" />
            )}
          </span>
        )}
      </button>

      {isChildSwitcherOpen && (
        <div
          data-testid="child-switcher-dropdown"
          className={clsx(
            'absolute z-20 rounded-2xl bg-white p-4 shadow-xl',
            collapsed
              ? 'left-full top-0 ml-2 w-70 rtl:right-full rtl:left-auto rtl:ml-0 rtl:mr-2'
              : 'left-0 right-0 top-full mt-2'
          )}
        >
          <p className="mb-2 text-sm font-semibold text-neutral-secondary">
            {formatText('sidebar.switch-child')}
          </p>
          <div className="flex flex-col gap-1.5">
            {children?.map((child) => {
              const isSelected = child.id === selectedChildId;

              return (
                <button
                  key={child.id}
                  type="button"
                  className={clsx(
                    'w-full flex items-center gap-2 rounded-lg p-2 text-start transition-all cursor-pointer',
                    isSelected
                      ? 'bg-neutral-light-50'
                      : 'hover:bg-neutral-light-50'
                  )}
                  onClick={() => {
                    onSelectChild(child.id);
                  }}
                >
                  {child.profile_img ? (
                    <img
                      src={child.profile_img}
                      alt={child.name}
                      className="h-10 w-10 rounded-md border border-neutral-light-100 object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-md border border-neutral-light-100 bg-neutral-light-50 flex items-center justify-center text-xs font-semibold text-neutral-primary">
                      {child.name
                        .split(' ')
                        .slice(0, 2)
                        .map((word) => word.charAt(0))
                        .join('')
                        .toUpperCase()}
                    </div>
                  )}
                  <p className="text-sm font-semibold text-neutral-primary">
                    {child.name}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface ISidebarMenuItemProps {
  item: IMenuItem;
  collapsed: boolean;
  hoveredMenu: TMenuId;
  isActive: (path: string) => boolean;
  formatText: (id: string) => string;
  setHoveredMenu: (id: TMenuId) => void;
  userRole?: EUserRole | null;
}

const SidebarMenuItem = ({
  item,
  collapsed,
  hoveredMenu,
  isActive,
  formatText,
  setHoveredMenu,
  userRole,
}: ISidebarMenuItemProps) => {
  const resolvedLink =
    (userRole && item.roleLinks?.[userRole]) || item?.link || '';

  return (
    <div
      key={item.id}
      className="relative w-full"
      onMouseEnter={() => collapsed && setHoveredMenu(item.id)}
      onMouseLeave={() => collapsed && setHoveredMenu(null)}
    >
      <Link
        to={item?.disabled ? '#' : resolvedLink}
        onClick={(e) => item?.disabled && e.preventDefault()}
        data-testid={`sidebar-menu-item-${item.id}`}
        className={clsx(
          'flex text-neutral-secondary hover:text-white p-2.5 gap-2 w-full items-center rounded-lg cursor-pointer aria-disabled:cursor-not-allowed transition-all delay-150 duration-150',
          isActive(resolvedLink)
            ? 'bg-brand-default shadow-sharp'
            : 'hover:bg-brand-hover',
          collapsed && JUSTIFY_CENTER,
          item?.hidden && 'hidden',
          item?.disabled && CURSOR_NOT_ALLOWED
        )}
      >
        {item.icon && (
          <item.icon
            stroke={1.5}
            className={clsx(
              isActive(resolvedLink) && TEXT_WHITE,
              collapsed && 'mx-auto'
            )}
            size={20}
          />
        )}

        {!collapsed && (
          <p
            className={clsx(
              'text-sm font-medium',
              isActive(resolvedLink) && TEXT_WHITE
            )}
          >
            {formatText(item?.name)}
          </p>
        )}
      </Link>

      {collapsed && hoveredMenu === item?.id && (
        <div className="absolute rtl:right-full rtl:top-1/2 rtl:translate-y--1/2 rtl:mr-2 left-full top-1/2 -translate-y-1/2 ml-2 z-50 pointer-events-none w-fit">
          <div className="bg-white text-xs text-neutral-primary font-medium px-3 py-1.5 rounded-md shadow-lg whitespace-nowrap">
            {formatText(item.name)}
          </div>
        </div>
      )}
    </div>
  );
};

const AppSidebar = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { collapsed, setCollapsed } = usePageTitle();
  const { width } = useWindowSize();
  const { user } = useUser();
  const { settings } = useSettingsStore();
  const brandLogoSrc =
    settings?.language === 'ar'
      ? '/assets/images/tirtir-world-ar.png'
      : '/assets/images/tirtir-world-en.png';
  const {
    children: parentChildren,
    selectedChild,
    setChildren,
    setSelectedChild,
  } = useParentStore();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isChildSwitcherOpen, setIsChildSwitcherOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const childSwitcherRef = useRef<HTMLDivElement>(null);

  const [hoveredMenu, setHoveredMenu] = useState<number | string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { formatText } = useFormatText();
  const isParentUser = user?.is_access === EUserRole.PARENT;

  // Fetch children list for parent users
  useEffect(() => {
    if (!isParentUser) return;
    const fetchChildren = async () => {
      const res = await getParentChildren();
      if (res?.status && res?.data) {
        setChildren(res.data as IParentChild[]);
      }
    };
    fetchChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParentUser]);

  useEffect(() => {
    if (width < 950) {
      setCollapsed(true);
    }
  }, [width, setCollapsed]);

  // Close popup on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        setProfileMenuOpen(false);
      }
      if (
        childSwitcherRef.current &&
        !childSwitcherRef.current.contains(target)
      ) {
        setIsChildSwitcherOpen(false);
      }
    }
    if (profileMenuOpen || isChildSwitcherOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen, isChildSwitcherOpen]);

  const isActive = (path: string) => {
    if (path === '/') {
      return currentPath === '/';
    }
    return currentPath.startsWith(path);
  };

  const handleLogout = async () => {
    setIsLoading(true);

    const deviceId = generateOrGetUserId();
    const response = await logoutApi(deviceId);
    if (response?.status) {
      enqueueSnackbar(response?.message, { variant: 'success' });
      localStorage.clear();
      window.location.reload();
    } else {
      setIsLoading(false);
      console.error(response?.message);
    }
    localStorage.clear();
    window.location.reload();
  };

  return (
    <div
      className={clsx(
        'h-screen p-3 flex flex-col items-start gap-6 bg-neutral-light-50 transition-all duration-200',
        collapsed ? '' : 'max-w-70 w-full'
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-between w-full transition-all delay-150 duration-150',
          collapsed && JUSTIFY_CENTER
        )}
      >
        {!collapsed && (
          <Link to="/" className="flex items-center">
            <CustomImage
              src={brandLogoSrc}
              alt={formatText('auth-layout.brand-alt')}
              className="h-12 w-auto select-none"
            />
          </Link>
        )}

        <div
          className={clsx(
            'py-2.5 px-1 rounded-lg justify-start cursor-pointer items-center gap-2 inline-flex  hover:bg-neutral-100',
            collapsed && 'mx-auto'
          )}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <IconLayoutSidebarLeftExpand
              stroke={1.5}
              size={24}
              color="#717784"
              className="hover:text-neutral-800 rtl:rotate-180"
            />
          ) : (
            <IconLayoutSidebarLeftCollapse
              stroke={1.5}
              size={24}
              color="#717784"
              className="hover:text-neutral-800 rtl:rotate-180"
            />
          )}
        </div>
      </div>
      <div
        className={clsx(
          'inline-flex h-full flex-col items-start gap-1 w-full flex-1',
          collapsed && 'items-center'
        )}
      >
        {isParentUser && selectedChild && parentChildren.length > 0 && (
          <ParentChildSwitcher
            collapsed={collapsed}
            children={parentChildren}
            selectedChild={selectedChild}
            selectedChildId={selectedChild.id}
            isChildSwitcherOpen={isChildSwitcherOpen}
            childSwitcherRef={childSwitcherRef}
            formatText={formatText}
            onToggle={() => setIsChildSwitcherOpen((prev) => !prev)}
            onSelectChild={(childId) => {
              const child = parentChildren?.find((c) => c.id === childId);
              if (child) setSelectedChild(child);
              setIsChildSwitcherOpen(false);
            }}
          />
        )}

        {sidebarMenu
          .filter(
            (item) =>
              !item.allowedRoles ||
              (user?.is_access && item.allowedRoles.includes(user.is_access))
          )
          .map((item) => (
            <SidebarMenuItem
              key={item.id}
              item={item}
              collapsed={collapsed}
              hoveredMenu={hoveredMenu}
              isActive={isActive}
              formatText={formatText}
              setHoveredMenu={setHoveredMenu}
              userRole={user?.is_access}
            />
          ))}
      </div>
      <div className="w-full mb-6">
        <SidebarMenuItem
          item={{
            id: 'help-support',
            name: 'sidebar.help-support',
            icon: IconHelp,
            link: '/help-support',
          }}
          collapsed={collapsed}
          hoveredMenu={hoveredMenu}
          isActive={isActive}
          formatText={formatText}
          setHoveredMenu={setHoveredMenu}
        />
      </div>
      <div
        className={clsx(
          'flex p-2 flex-col items-start gap-2 w-full rounded-lg border border-neutral-100 bg-white shadow-customInset relative bottom-8',
          collapsed && 'items-center ' + JUSTIFY_CENTER + ' p-2'
        )}
        ref={profileMenuRef}
      >
        <div
          className={clsx(
            'flex items-center gap-2 w-full relative',
            collapsed ? JUSTIFY_CENTER : 'justify-between'
          )}
        >
          <div
            className={clsx(
              'flex items-center gap-2 w-full cursor-auto',
              collapsed && JUSTIFY_CENTER + ' '
            )}
            onClick={() => {
              if (collapsed) setProfileMenuOpen((prev) => !prev);
            }}
          >
            <UserAvatarList
              user={user?.name}
              image={user?.profile_pic}
              className="w-10! h-10! min-w-10!"
              isTooltip={collapsed}
            />
            {!collapsed && (
              <div className="inline-flex flex-col justify-start items-start">
                <p className="text-sm font-semibold text-neutral-primary capitalize">
                  {user?.name}
                </p>
                <p className="text-sm font-light text-neutral-secondary truncate max-w-40">
                  {user?.email}
                </p>
              </div>
            )}

            {!collapsed && (
              <span
                className="text-neutral-primary font-medium  ms-auto shrink-0 cursor-pointer"
                onClick={handleLogout}
              >
                {isLoading ? (
                  <IconLoader2 className="animate-spin h-5 w-5" />
                ) : (
                  <IconLogout size={24} className="rtl:rotate-180" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppSidebar;
